import React, { createContext, useContext, useState } from "react";

const UIContext = createContext();

/**
 * Context for managing UI state and interactions, such as alerts.
 * Provides a way to access and manipulate UI related state across the application.
 */
export const useUI = () => useContext(UIContext);

/**
 * Provider component for the UIContext.
 * It encapsulates the logic for showing and hiding alerts, and any other UI related state management.
 * @param {object} props - The props object.
 * @param {ReactNode} props.children - The child components that will have access to the UIContext.
 * @returns {JSX.Element} The UIContext.Provider component wrapping the children.
 */
export const UIProvider = ({ children }) => {
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [isLoading, setIsLoading] = useState(false);

  /**
   * Function to show an alert.
   * Updates the alert state to make the alert visible with the specified message and severity.
   * @param {string} message - The message to be displayed in the alert.
   * @param {string} severity - The severity of the alert. Defaults to "info".
   */
  const showAlert = (message, severity = "info") => {
    setAlert({ open: true, message, severity });
  };

  /**
   * Function to close the alert.
   * Updates the alert state to hide the alert.
   */
  const closeAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  // Function to show the loading spinner
  const showLoadingSpinner = () => {
    setIsLoading(true);
  };

  // Function to hide the loading spinner
  const hideLoadingSpinner = () => {
    setIsLoading(false);
  };

  return (
    <UIContext.Provider value={{ ...alert, showAlert, closeAlert, showLoadingSpinner, hideLoadingSpinner, isLoading }}>
      {children}
    </UIContext.Provider>
  );
};
